import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { lazy, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { redirect, useLoaderData, useLocation, defer } from "@remix-run/react";
import ListingsService from "~/lib/services/ListingsService";
import { Listing } from "~/components/blocks/common/Interfaces/ListingsInterface";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import { useFavorite } from "~/lib/hooks/useFavourite";
import { getDeviceInfo } from "~/lib/hooks/useViewportListener";
const MobileLayout = lazy(() => import("../../components/blocks/ListingsPage/MobileLayout"));
const DesktopLayout = lazy(() => import("../../components/blocks/ListingsPage/DesktopLayout"));
import { areaSlug, listingCanonical, listingSlug, idFromSlugParam } from "~/lib/helpers/slugHelper";
import { factsFromListing } from "~/helpers/QuickFactsHelper";
import { currentURL, handleCountryAndSlugRedirect } from "~/helpers/UrlHelper";
import { ListingMeta } from "~/utils/Meta/listingMeta";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import { getConfig, regionConfigs } from "custom-config/region.configs";
import { recordListingView, recordListingEngagement, collectImpressions } from "~/lib/Telemetry";
import { convertSpacesToDashes } from "~/lib/helpers/StringHelper";
// import { checkBotId } from "~/lib/utils/botid";

export const loader = async ({ params, request }: LoaderFunctionArgs) => {

  // ========================================
  // 🛡️ BOT PROTECTION - CHECK FIRST!
  // ========================================
  // const botCheck = await checkBotId(request);

  // if (botCheck.isBot) {
  //   console.warn("Bot detected on listing page:", {
  //     listingId: params.listingId,
  //     userAgent: request.headers.get("user-agent"),
  //     ip: request.headers.get("cf-connecting-ip"),
  //   });

  //   // Option 1: Block completely (recommended for scrapers)
  //   throw json(
  //     { error: "Access denied" },
  //     { status: 403 }
  //   );

  // Option 2: Allow but log (uncomment if you want softer approach)
  // This lets legitimate crawlers like Google through
  // console.log("Bot detected but allowing request");
  // }
  // ========================================
  // END BOT PROTECTION
  // ========================================


  const { isMobile } = getDeviceInfo(request, 768)
  const { listingId = "", country } = params
  const regionData = getConfig((country || DEFAULT_REGION) as keyof typeof regionConfigs) ?? undefined;
  const id = idFromSlugParam(listingId)

  // strict 15 or 18 char SFID
  const isSFID = typeof id === "string" && /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/.test(id)

  let listing: Listing = {} as Listing

  try {
    if (isSFID) {
      listing = await ListingsService.getListingDetailsByID(id!)

      if (!listing || !listing.id) {
        throw new Response("Listing not found", { status: 404 })
      }

      // Handle country and slug redirects via reusable utility
      const redirectPath = handleCountryAndSlugRedirect({
        entity: listing,
        country,
        request,
        getCanonicalSlug: listingCanonical,
      })
      if (redirectPath) {
        return redirect(redirectPath, 301)
      }

      // If no redirect needed, continue

      // Start fetching secondary data (defer to avoid blocking)
      const propertyListingsPromise = ListingsService.fetchPropertyListings(listing?.propertyId)
        .catch(() => []);

      const allAvailableOptionsPromise = ListingsService.getMetadataListingsByProperty(listing?.propertyId)
        .catch(() => []);

      return defer({
        listing,
        country,
        isMobile,
        regionData,
        propertyListings: propertyListingsPromise,
        allAvailableOptions: allAvailableOptionsPromise,
      });
    } else {
      throw new Response("Invalid listing ID format", { status: 400 })
    }
  } catch (error) {
    if (error instanceof Response) {
      throw error
    }
    throw new Response("Error fetching listing", { status: 404 })
  }
}
export const meta: MetaFunction = ({ data, location }) => {
  // Defensive: fallback for missing data/location
  if (!data || !location) return [];

  return ListingMeta(data, location.pathname || '/');
};


export default function Index() {
  const { listing, country, isMobile, propertyListings: propertyListingsPromise, allAvailableOptions: allAvailableOptionsPromise } = useLoaderData<typeof loader>();
  const { liked, toggleFavorite } = useFavorite(listing.id);
  const { currency } = useCurrentCurrency();
  const location = useLocation();
  let { pathname } = location;
  const engagementTracked = useRef(false);
  const viewTracked = useRef(false);
  // Below Section Commented for Now will revise the logic again
  // const [popularSearches, setPopularSearches] = useState([]);
  const [nearbyOffices, setNearbyOffices] = useState([]);
  const [officesAround, setOfficesAround] = useState([]);

  const fetchRelatedListings = useCallback(async () => {
    const results = await Promise.allSettled([
      ListingsService.getNearByOffices(
        listing.geolocationLat,
        listing.geolocationLong,
        15,
        listing.noOfWS,
        listing.spaceTypeFormula
      ),
      ListingsService.getOfficesAround(
        convertSpacesToDashes(listing.suburb || listing.city),
        listing?.sfid,
        listing?.geolocationLat,
        listing?.geolocationLong,
        10,
        undefined,
        listing.country

      ),
      // Below Section Commented for Now will revise the logic again
      // ),
      // ListingsService.getPopularSearches(
      //   listing.geolocationLat,
      //   listing.geolocationLong,
      //   listing.suburb || listing.city,
      //   listing.spaceTypeFormula
      //     ? OFFICE_TYPE_LABEL[listing.spaceTypeFormula].spaceType
      //     : "",
      //   6
      // )
    ]);



    // Handling each promise result individually
    const [nearbyOfficesResult, officesAroundResult] =
      results;

    if (nearbyOfficesResult.status === 'fulfilled') {
      setNearbyOffices(nearbyOfficesResult.value || []);
    } else {
      console.error('Failed to fetch nearby locations:', nearbyOfficesResult.reason);
    }

    if (officesAroundResult.status === 'fulfilled') {
      setOfficesAround(officesAroundResult.value || []);
    } else {
      console.error('Failed to fetch nearby areas:', officesAroundResult.reason);
    }
    // Below Section Commented for Now will revise the logic again
    // if (popularSearchesResult.status === 'fulfilled') {
    //   setPopularSearches(popularSearchesResult.value || []);
    // }
    // else {
    //   console.error('Failed to fetch area office types:', popularSearchesResult.reason);
    // }
  }, [listing]);

  useEffect(() => {
    // Use requestIdleCallback for non-critical data fetching to improve performance
    // Falls back to setTimeout if requestIdleCallback is not available
    let idleCallbackId: number | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    if ('requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(() => {
        fetchRelatedListings();
      }, { timeout: 2000 }) as unknown as number;
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      timeoutId = setTimeout(fetchRelatedListings, 1500);
    }

    return () => {
      if (idleCallbackId !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [fetchRelatedListings]);

  // Track listing view and impressions - only once when page loads
  useEffect(() => {
    if (listing?.id && !viewTracked.current) {
      viewTracked.current = true;

      // Track the view immediately (critical)
      recordListingView(
        listing.id,
        listing.propertyId || null,
        listing?.workspaceProvider?.id || null,
        listing.buildingId || null
      );

      // Collect impressions after view is recorded - use requestIdleCallback
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          collectImpressions();
        }, { timeout: 1000 });
      } else {
        setTimeout(() => {
          collectImpressions();
        }, 1000);
      }
    }
  }, [listing?.id]);

  // Track listing engagement on any click
  useEffect(() => {
    const handleClick = () => {
      // Only track engagement once per page visit
      if (!engagementTracked.current && listing?.id) {
        engagementTracked.current = true;
        recordListingEngagement(
          listing.id,
          listing.propertyId || null,
          listing.workspaceProvider?.id || null,
          listing.buildingId || null
        );
      }
    };

    // Add click listener to document
    document.addEventListener('click', handleClick);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [listing?.id, listing?.propertyId, listing?.workspaceProvider?.id, listing?.buildingId]);

  const address = useMemo(() => {
    return `${listing.address}${listing.city ? ", " + listing.city : ""
      }${listing.state && listing.state !== listing.city
        ? ", " + listing.state
        : ""
      }${listing.zipcode ? ", " + listing.zipcode : ""}`
  }, [listing.address, listing.city, listing.state])
  const memoizedFacts = useMemo(() => factsFromListing(listing, currency), [listing, currency]);
  const memoizedUrl = useMemo(() => currentURL(country, listing, "listing"), [country, listing]);

  const Layout = isMobile ? MobileLayout : DesktopLayout;


  return (
    <Layout
      listing={listing}
      liked={liked}
      toggleFavorite={toggleFavorite}
      listingOptions={allAvailableOptionsPromise}
      region={country}
      path={pathname}
      currency={currency}
      propertyListings={propertyListingsPromise}
      officesAround={officesAround}
      nearbyOffices={nearbyOffices}
      nearbyOfficesSlug={listingSlug} officesAroundSlug={areaSlug}
      isListingsPage={true}
      facts={memoizedFacts}
      url={memoizedUrl}
      fetchRelatedListings={fetchRelatedListings}
      workspace={listing?.workspaceData}
      address={address}
      heading={listing?.webPrimaryKeyword}
      listingId={listing.id}
    />
  )
}